/**
 * Give someone a membership.
 *
 * A comp is the one membership no payment produced — staff, contributing
 * editors, advertisers, award winners. Before this screen the only way to
 * make one was to key it in SimpleCirc and wait for the sync.
 *
 * Three things the layout is trying to do:
 *
 *   - Put the duplicate warning in front of the operator BEFORE they save,
 *     not after. Someone being comped is very often already on file from a
 *     lapsed subscription, and a second record splits their history in two.
 *     The probe runs on the postal identity as it is typed, the same
 *     `/api/offline/match` the mailroom screen uses.
 *   - Make the end date's blankness mean something visible. An empty date
 *     field reads as "not filled in yet"; here it means forever, which is
 *     the expensive option, so the screen says so in words next to it.
 *   - Refuse to be submitted twice. Every save carries a `form_token` minted
 *     with `crypto.randomUUID()`; the backend refuses a token it has already
 *     seen, and a fresh one is minted after each success.
 *
 * Comping someone ALREADY on file is deliberately not here — the ~180k
 * SimpleCirc-sourced rows are rewritten by the nightly sync, so a local edit
 * to one would be reverted without a trace. The duplicate warning links out
 * to the member instead.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, type OfflineCandidate } from '../api/client'
import { Button } from '../components/Button'
import { Field } from '../components/Field'
import { Panel } from '../components/Panel'
import { describeError } from '../lib/describeError'
import { PUBLICATION_NAMES } from '../lib/format'
import { useDebounced } from '../lib/useDebounced'

type CompResult = {
  subscriber_id: number
  member_account_number: string
  subscriptions: Array<{
    publication_slug: string
    subscription_id: number
    entitlement_reason: string
  }>
  sign_in_link_sent: boolean
}

type Draft = {
  first_name: string
  last_name: string
  company: string
  address_1: string
  address_2: string
  city: string
  state: string
  zipcode: string
  email: string
  phone: string
}

const EMPTY: Draft = {
  first_name: '', last_name: '', company: '',
  address_1: '', address_2: '', city: '', state: '', zipcode: '',
  email: '', phone: '',
}

const SLUGS = Object.keys(PUBLICATION_NAMES)

export function AddCompPage() {
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [titles, setTitles] = useState<string[]>([])
  const [through, setThrough] = useState('')
  const [reason, setReason] = useState('')
  const [sendLink, setSendLink] = useState(false)
  const [token, setToken] = useState(() => crypto.randomUUID())
  const [done, setDone] = useState<CompResult | null>(null)

  const set = (k: keyof Draft) => (e: { target: { value: string } }) =>
    setDraft({ ...draft, [k]: e.target.value })

  // Same probe as the mailroom screen: a surname plus at least one locating
  // field before it means anything.
  const probe = useDebounced({
    first_name: draft.first_name.trim() || null,
    last_name: draft.last_name.trim() || null,
    address_1: draft.address_1.trim() || null,
    zipcode: draft.zipcode.trim() || null,
    email: draft.email.trim() || null,
    phone: draft.phone.trim() || null,
  }, 500)
  const probeReady = Boolean(
    probe.last_name && (probe.zipcode || probe.address_1 || probe.email),
  )
  const match = useQuery({
    queryKey: ['comp-match', probe],
    queryFn: () => api.post<{ candidates: OfflineCandidate[] }>('/api/offline/match', probe),
    enabled: probeReady && !done,
    staleTime: 30_000,
  })
  const candidates = match.data?.candidates ?? []

  const usable = useMemo(() => (
    Boolean((draft.last_name.trim() || draft.company.trim())
      && titles.length > 0
      && reason.trim()
      && (!sendLink || draft.email.trim()))
  ), [draft, titles, reason, sendLink])

  const create = useMutation({
    mutationFn: () => api.post<CompResult>('/api/comps', {
      form_token: token,
      first_name: draft.first_name.trim() || null,
      last_name: draft.last_name.trim() || null,
      company: draft.company.trim() || null,
      address_1: draft.address_1.trim() || null,
      address_2: draft.address_2.trim() || null,
      city: draft.city.trim() || null,
      state: draft.state.trim() || null,
      zipcode: draft.zipcode.trim() || null,
      country: 'US',
      email: draft.email.trim() || null,
      phone: draft.phone.trim() || null,
      publication_slugs: titles,
      comped_through: through || null,
      reason: reason.trim(),
      send_sign_in_link: sendLink,
    }),
    onSuccess: (res) => setDone(res),
  })

  function startAnother() {
    setDraft(EMPTY)
    setTitles([])
    setThrough('')
    setReason('')
    setSendLink(false)
    setToken(crypto.randomUUID())
    setDone(null)
    create.reset()
  }

  if (done) {
    return (
      <div className="max-w-[720px] space-y-5">
        <Panel label="Comp created" accent="green">
          <p className="text-sm text-paper mb-4">
            Account number <span className="font-mono text-amber-glow">{done.member_account_number}</span>
            {' — '}this is what they quote, along with their ZIP, to sign in.
          </p>
          <ul className="space-y-1.5 mb-4">
            {done.subscriptions.map((s) => (
              <li key={s.subscription_id} className="font-mono text-[13px] text-paper-dim">
                {PUBLICATION_NAMES[s.publication_slug] ?? s.publication_slug}
                {' · '}
                <span className="text-paper">{s.entitlement_reason}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-paper-dim mb-5">
            {done.sign_in_link_sent
              ? 'A sign-in link has been sent.'
              : 'No email was sent. Their member record can send one at any time.'}
          </p>
          <div className="flex items-center gap-3">
            <Link to={`/subscribers/${done.subscriber_id}`}>
              <Button size="sm" type="button">Open the member</Button>
            </Link>
            <Button variant="ghost" size="sm" type="button" onClick={startAnother}>
              Comp someone else
            </Button>
          </div>
        </Panel>
      </div>
    )
  }

  return (
    <div className="max-w-[720px] space-y-5">
      <Panel label="Who is being comped">
        {candidates.length > 0 && (
          <div className="border border-amber-glow/50 bg-amber-wash px-4 py-3 mb-5">
            <div className="label-amber mb-2">
              Already on file?{' '}
              {candidates.length === 1 ? 'One member looks' : `${candidates.length} members look`} like this
            </div>
            <div className="space-y-1.5">
              {candidates.map((c) => (
                <Link
                  key={c.subscriber_id}
                  to={`/subscribers/${c.subscriber_id}`}
                  className="block font-mono text-[12px] text-paper-dim hover:text-amber-glow"
                >
                  {c.name} · {c.account_number ?? 'no account number'} · {c.zipcode ?? '—'}
                </Link>
              ))}
            </div>
            <p className="text-[11px] text-paper-trace mt-2 max-w-[60ch]">
              Comping an existing member is not done from this screen — their
              subscription is owned by SimpleCirc and a change here would be
              overwritten by the nightly sync. Comp them there.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field label="First name" value={draft.first_name} onChange={set('first_name')} />
          <Field label="Last name" value={draft.last_name} onChange={set('last_name')} />
          <Field label="Company" value={draft.company} onChange={set('company')} />
          <Field label="Email" value={draft.email} onChange={set('email')} />
          <Field label="Address" value={draft.address_1} onChange={set('address_1')} />
          <Field label="Address 2" value={draft.address_2} onChange={set('address_2')} />
          <Field label="City" value={draft.city} onChange={set('city')} />
          <Field label="State" value={draft.state} onChange={set('state')} />
          <Field label="ZIP" value={draft.zipcode} onChange={set('zipcode')} />
          <Field label="Phone" value={draft.phone} onChange={set('phone')} />
        </div>
        <p className="text-[11px] text-paper-trace mt-3 max-w-[62ch]">
          Account number + ZIP is the whole sign-in for a member with no email
          address, so the ZIP is worth keying even for a comp.
        </p>
      </Panel>

      <Panel label="What they get" accent="cyan">
        <div className="label mb-2">Titles</div>
        <div className="grid grid-cols-2 gap-y-2 mb-6">
          {SLUGS.map((slug) => (
            <label key={slug} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="accent-amber-glow"
                checked={titles.includes(slug)}
                onChange={(e) => setTitles(e.target.checked
                  ? [...titles, slug]
                  : titles.filter((s) => s !== slug))}
              />
              <span className="font-mono text-[13px] text-paper">{PUBLICATION_NAMES[slug]}</span>
            </label>
          ))}
        </div>

        <Field
          label="Comped through"
          type="date"
          value={through}
          onChange={(e) => setThrough(e.target.value)}
          onClear={through ? () => setThrough('') : undefined}
        />
        <p className="text-[11px] text-paper-trace mt-2 max-w-[62ch]">
          {through
            ? 'Access ends on this date. It lapses on the day — a comp gets no grace window, because there is no payment in flight to wait for.'
            : 'Left blank, this comp never expires. Nothing will ever lapse it, and nothing will ever bill it.'}
        </p>
      </Panel>

      <Panel label="Why" accent="violet">
        <Field
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Contributing editor · advertiser trade · 2026 award winner"
        />
        <p className="text-[11px] text-paper-trace mt-2 max-w-[62ch]">
          Recorded on the audit trail. This is the only record of why a
          membership was given away.
        </p>

        <label className="flex items-center gap-2 mt-5 cursor-pointer">
          <input
            type="checkbox"
            className="accent-amber-glow"
            checked={sendLink}
            onChange={(e) => setSendLink(e.target.checked)}
          />
          <span className="font-mono text-[13px] text-paper">Send them a sign-in link now</span>
        </label>
        {sendLink && !draft.email.trim() && (
          <p className="text-[11px] text-radar-red mt-2">An email address is needed to send a link.</p>
        )}
      </Panel>

      {create.isError && (
        <p className="border border-radar-red/50 bg-radar-red/10 px-4 py-3 text-sm text-radar-red">
          {describeError(create.error)}
        </p>
      )}

      <div className="flex items-center gap-3">
        <Button
          type="button"
          disabled={!usable || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'Saving…' : 'Create the comp'}
        </Button>
        {!usable && (
          <span className="text-[11px] text-paper-trace">
            A surname or a company, at least one title, and a reason.
          </span>
        )}
      </div>
    </div>
  )
}
